import axiosInstance from '@/app/api/axiosInstance';
import type {
    Category,
    ItemListResponse,
    ItemSimple,
    PricedItem,
    PricingQuote,
    TaxRate,
    Unit,
} from '@/app/types/item';

/**
 * Live catalog queries.
 *
 * Products are fetched from the API on every search / category change. This app
 * deliberately keeps NO local product catalog (the ERP frontend mirrors items into
 * IndexedDB; we don't). Only three tiny reference tables — units, tax rates and
 * categories, 34 rows between them — are cached in memory for the session.
 */

export const ITEM_PAGE_SIZE = 50;

/** Filter shape the backend list endpoints accept as a JSON string. */
type Filter = { field: string; op: string; value: unknown };

const buildFilters = (filters: Filter[]) => JSON.stringify(filters);

/* ------------------------------------------------------------------ *
 * Reference lookups (small, cache with staleTime: Infinity)
 * ------------------------------------------------------------------ */

export const listUnits = async (): Promise<Unit[]> => {
    const response = await axiosInstance.get<{ items: Unit[] }>('/units/', {
        params: { limit: 200 },
    });
    return response.data.items || [];
};

export const listTaxRates = async (): Promise<TaxRate[]> => {
    const response = await axiosInstance.get<{ items: TaxRate[] }>('/tax-rates/', {
        params: { limit: 100 },
    });
    return response.data.items || [];
};

export const listCategories = async (): Promise<Category[]> => {
    const response = await axiosInstance.get<{ items: Category[] }>('/categories/', {
        params: { limit: 200, sort_by: 'name', sort_dir: 'asc' },
    });
    return (response.data.items || []).filter((c) => c.is_active);
};

/* ------------------------------------------------------------------ *
 * Live item queries
 * ------------------------------------------------------------------ */

interface ListItemsOptions {
    searchQuery?: string;
    categoryId?: number | null;
    limit?: number;
    skip?: number;
}

/**
 * Lean item list. `include_details=false` returns base_unit_id + tax_rate_id +
 * category_id, which is what a sales-order line needs. It does NOT return prices
 * (they always come back null) — use loadPricedItems to get a fully priced list.
 */
export const listItems = async ({
    searchQuery,
    categoryId,
    limit = ITEM_PAGE_SIZE,
    skip = 0,
}: ListItemsOptions): Promise<ItemListResponse> => {
    const filters: Filter[] = [{ field: 'is_active', op: 'eq', value: true }];
    if (categoryId) {
        filters.push({ field: 'category_id', op: 'eq', value: categoryId });
    }

    const params: Record<string, unknown> = {
        include_details: false,
        limit,
        skip,
        sort_by: 'name',
        sort_dir: 'asc',
        filters: buildFilters(filters),
    };
    if (searchQuery) {
        params.search_query = searchQuery;
    }

    const response = await axiosInstance.get<ItemListResponse>('/items/', { params });
    return response.data;
};

/** Fetch specific items by id (used to resolve previously-ordered products). */
export const listItemsByIds = async (itemIds: number[]): Promise<ItemSimple[]> => {
    if (itemIds.length === 0) return [];
    const response = await axiosInstance.get<ItemListResponse>('/items/', {
        params: {
            include_details: false,
            limit: 500,
            filters: buildFilters([
                { field: 'id', op: 'in', value: itemIds },
                { field: 'is_active', op: 'eq', value: true },
            ]),
        },
    });
    return response.data.items || [];
};

/**
 * Who a page of items is priced for.
 *
 * - partyId: a new order, priced in the party's current mode.
 * - documentId: editing an existing order, priced in the mode stamped on it.
 *
 * The backend decides the mode; this app never does (CLIENT_PRICING_CONTRACT.md).
 */
export type PricingTarget =
    | { partyId: number; siteId: number | null }
    | { documentId: number; siteId: number | null };

/** Final rates and ladders for a page of items, from the server. */
export const quotePricing = async (
    itemIds: number[],
    target: PricingTarget
): Promise<PricingQuote> => {
    const response = await axiosInstance.post<PricingQuote>('/pricing/quote', {
        item_ids: itemIds,
        site_id: target.siteId,
        ...('documentId' in target
            ? { document_id: target.documentId }
            : { party_id: target.partyId }),
    });
    return response.data;
};

/* ------------------------------------------------------------------ *
 * Joining
 * ------------------------------------------------------------------ */

const toNumber = (value: string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export interface PricedPage {
    items: PricedItem[];
    /** Badge text from the server, e.g. "Credit pricing"; null for standard. */
    priceModeLabel: string | null;
}

/**
 * Join a page of items with their quoted price ladders.
 *
 * One round trip per page: the server resolves rules, site prices and the
 * party's pricing mode, so nothing about pricing is computed here.
 */
export const attachPricing = async (
    items: ItemSimple[],
    units: Unit[],
    taxRates: TaxRate[],
    target: PricingTarget
): Promise<PricedPage> => {
    if (items.length === 0) return { items: [], priceModeLabel: null };

    const quote = await quotePricing(items.map((i) => i.id), target);

    const unitById = new Map(units.map((u) => [u.id, u]));
    const taxById = new Map(taxRates.map((t) => [t.id, t]));
    const quoteById = new Map(quote.items.map((q) => [q.item_id, q]));

    return {
        priceModeLabel: quote.price_mode_label ?? null,
        items: items.map((item) => {
            const quoted = quoteById.get(item.id);
            const taxRate = item.tax_rate_id ? taxById.get(item.tax_rate_id) : undefined;

            return {
                ...item,
                unitName: unitById.get(item.base_unit_id)?.name ?? 'Unit',
                taxPercent: toNumber(taxRate?.rate) ?? 0,
                isTaxInclusive: quoted?.is_tax_inclusive ?? false,
                tiers: (quoted?.price_tiers ?? []).map((tier) => ({
                    minQty: toNumber(tier.min_qty) ?? 0,
                    maxQty: toNumber(tier.max_qty),
                    rate: toNumber(tier.rate) ?? 0,
                    standardRate: toNumber(tier.standard_rate),
                    label: tier.label?.trim() || null,
                })),
            };
        }),
    };
};

/** Convenience: one live page of products, fully priced. */
export const loadPricedItems = async (
    options: ListItemsOptions,
    units: Unit[],
    taxRates: TaxRate[],
    target: PricingTarget
): Promise<PricedPage & { total: number }> => {
    const { items, total } = await listItems(options);
    const priced = await attachPricing(items, units, taxRates, target);
    return { ...priced, total };
};
